import { Location } from 'history'
import { useEffect, useRef } from 'react'
import { MutationOptions, useMutation } from '@tanstack/react-query'
import { fetchIdentityProviderUrl } from 'api/modules/authentication'
import { ErrorCodes } from '@commutifi-fe/constants'
import { useAsyncFn } from 'shared/hooks/useAsyncFn'
import { extractUrlSearchParams } from 'utils/index'
import { logger } from 'utils/logRocket'
import { useSafeSetState } from '../global'
import { stateStorage } from './utils'

export { useOauthSender } from './useOauthSender'

interface IUseGetTokenProps<T = any> {
  // Function used to extract the auth token
  tokenFn: (body: any) => T
  // Error callback with the error object passed down
  onError?: (e: any) => void
  // Success callback with the response passed down
  onSuccess?: (res: T) => void
  location?: Location
}

interface IUseGetTokenReturnValue<T = any> {
  processing: boolean
  error: any
  res: Awaited<T>
  state: any
}

interface IUseLoginRedirectResult<T> {
  loading: boolean
  error: any
  loginWithRedirect: (providerId: string, state: T) => Promise<void>
}

export interface IGetOauthResponse {
  redirectUrl: string
}

/**
 * Extract the state and code from the Oauth redirect URl and prepare
 * API request to extract the authorization token by injecting the state
 * and the authorizationCode in the body of the request, alongside with the
 * extra body when needed.
 * @param useTokenProps - see [[IUseGetTokenProps]] for more details
 */
export function useGetToken<T>({
  tokenFn,
  onSuccess,
  onError,
  location
}: IUseGetTokenProps<T>): IUseGetTokenReturnValue<T> {
  const [receiverState, setReceiverState] = useSafeSetState({
    processing: true,
    error: null,
    res: null,
    state: stateStorage.extractState(extractUrlSearchParams(location).state) || {}
  })

  // Fix useEffect deps warning: https://github.com/facebook/react/issues/15282#issuecomment-479021454
  const latestSetReceiverState = useRef(setReceiverState)
  useEffect(() => {
    latestSetReceiverState.current = setReceiverState
  }, [setReceiverState])

  const latestTokenFn = useRef(tokenFn)
  useEffect(() => {
    latestTokenFn.current = tokenFn
  }, [tokenFn])

  useEffect(() => {
    const fetchToken = async () => {
      let payload = {}
      try {
        const q = extractUrlSearchParams(location)

        const state = stateStorage.extractState(q.state) || {}
        payload = { processing: false, state }
        if (!q.code && !q.token && !q.id_token) {
          logger.warn(`${tokenFn.name} not triggered because there is no code in the URL query params`)

          // Not able to call token function -> generic error with default code and message
          latestSetReceiverState.current({
            ...payload,
            error: {
              data: { name: q.error || ErrorCodes.AccessDenied, message: q.error_description ?? 'no auth code' }
            }
          })
          return
        }

        const code = q.code || q.token || q.id_token
        // eslint-disable-next-line @typescript-eslint/await-thenable -- Legacy, taking no chances
        const res = await latestTokenFn.current({
          authorizationCode: code,
          subdomain: state.subdomain,
          state: q.state,
          nonce: q.state
        })
        onSuccess && onSuccess(res)
        latestSetReceiverState.current({ ...payload, res })
      } catch (error) {
        onError && onError(error)
        latestSetReceiverState.current({ ...payload, error })
      }
    }

    fetchToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy
  }, [location?.search])
  return receiverState
}

/**
 * Call api-dashboard to fetch the redirect url related to a
 * specific identity provider
 *
 */
export const useLoginWithRedirect = <T extends Record<string, any>>(): IUseLoginRedirectResult<T> => {
  const [{ loading, error }, fetchLoginUrl] = useAsyncFn(fetchIdentityProviderUrl)
  const handleProviderLogin = async (providerId: string, state: T) => {
    const nonce = await stateStorage.saveState(state)
    const { redirectUrl } = await fetchLoginUrl(providerId, {
      state: nonce
    })
    window.location.href = redirectUrl
  }
  return { loading, error, loginWithRedirect: handleProviderLogin }
}

/**
 * Call api-dashboard to fetch the OAuth redirect url and redirects
 * the user to the authorization url
 *
 * @param redirectFn - Function to call that will return the redirectUrl
 */
export function useGetOauthUrlWithRedirect<T extends { queryParams: { state?: Record<string, any> } } = any>(
  redirectFn: (params: any) => Promise<IGetOauthResponse>,
  config: MutationOptions<void, unknown, T> = {}
) {
  const { mutate: connectWithRedirect, ...queryInfo } = useMutation({
    mutationFn: async ({ queryParams, ...rest }: T) => {
      const nonce = await stateStorage.saveState(queryParams.state)
      const res = await redirectFn({ ...rest, queryParams: { ...queryParams, state: nonce } })
      if (res.redirectUrl) {
        window.location.href = res.redirectUrl
      }
    },
    ...config
  })
  return { ...queryInfo, connectWithRedirect }
}
